/**
 * Tiny SVG construction kit shared by every asset builder.
 *
 * Each builder returns an SVG *string*. BootScene turns that string into a
 * Blob URL and hands it to Phaser's SVG loader, which rasterizes it to a
 * texture. Keeping everything as plain strings means assets are 100%
 * programmatic — no binary files to manage — and a single palette change
 * re-flows through all of them.
 *
 * Convention: a builder's coordinate space (its viewBox) is in "logical
 * pixels". The same numbers are used for layout in the game, and BootScene
 * rasterizes at RENDER_SCALE for crispness on high-DPI screens.
 */
import { INK, STROKE } from './palette.ts';

export interface ShapeOpts {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  /** Extra raw attributes, e.g. `transform="rotate(10)"`. */
  extra?: string;
}

function attrs(o: ShapeOpts = {}): string {
  const fill = o.fill ?? 'none';
  const stroke = o.stroke ?? INK;
  const sw = o.strokeWidth ?? STROKE;
  const parts = [
    `fill="${fill}"`,
    stroke === 'none' ? 'stroke="none"' : `stroke="${stroke}"`,
    stroke === 'none' ? '' : `stroke-width="${sw}"`,
    // Rounded joins/caps everywhere => the "thick rounded outline" look.
    'stroke-linejoin="round"',
    'stroke-linecap="round"',
    o.opacity != null ? `opacity="${o.opacity}"` : '',
    o.extra ?? '',
  ];
  return parts.filter(Boolean).join(' ');
}

export const circle = (cx: number, cy: number, r: number, o?: ShapeOpts): string =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" ${attrs(o)} />`;

export const ellipse = (
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  o?: ShapeOpts,
): string => `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" ${attrs(o)} />`;

export const rect = (
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  o?: ShapeOpts,
): string =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" ry="${r}" ${attrs(o)} />`;

export const path = (d: string, o?: ShapeOpts): string => `<path d="${d}" ${attrs(o)} />`;

export const line = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  o?: ShapeOpts,
): string => `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" ${attrs(o)} />`;

export const polygon = (points: Array<[number, number]>, o?: ShapeOpts): string =>
  `<polygon points="${points.map((p) => p.join(',')).join(' ')}" ${attrs(o)} />`;

export const group = (children: string, transform?: string): string =>
  `<g ${transform ? `transform="${transform}"` : ''}>${children}</g>`;

/**
 * Wrap shape markup in a complete SVG document. width/height are set equal to
 * the viewBox so the rasterizer knows the natural size; BootScene multiplies
 * by RENDER_SCALE.
 */
export const svgDoc = (w: number, h: number, body: string): string =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" ` +
  `viewBox="0 0 ${w} ${h}">${body}</svg>`;

/** A happy curved-up smile, as a stroked path centered at (cx, cy). */
export const smile = (cx: number, cy: number, w: number, h: number): string =>
  path(`M ${cx - w / 2} ${cy} Q ${cx} ${cy + h} ${cx + w / 2} ${cy}`, {
    fill: 'none',
  });

// ── Gradients ────────────────────────────────────────────────────────────────
// Soft shading gives the art its rounded, storybook look. Gradients are scoped
// per-SVG document (each texture rasterizes alone), so simple ids are safe.
export type Stop = [offset: number, color: string, opacity?: number];

const stopsStr = (stops: Stop[]): string =>
  stops
    .map(([o, c, op]) => `<stop offset="${o}" stop-color="${c}"${op != null ? ` stop-opacity="${op}"` : ''}/>`)
    .join('');

/** Radial gradient placed in objectBoundingBox space (cx,cy,r in 0..1). */
export const radial = (
  id: string,
  stops: Stop[],
  o: { cx?: number; cy?: number; r?: number } = {},
): string =>
  `<radialGradient id="${id}" cx="${o.cx ?? 0.4}" cy="${o.cy ?? 0.35}" r="${o.r ?? 0.75}">${stopsStr(stops)}</radialGradient>`;

/** Linear gradient (x1,y1,x2,y2 in 0..1; default top→bottom). */
export const linear = (
  id: string,
  stops: Stop[],
  o: { x1?: number; y1?: number; x2?: number; y2?: number } = {},
): string =>
  `<linearGradient id="${id}" x1="${o.x1 ?? 0}" y1="${o.y1 ?? 0}" x2="${o.x2 ?? 0}" y2="${o.y2 ?? 1}">${stopsStr(stops)}</linearGradient>`;

export const defs = (content: string): string => `<defs>${content}</defs>`;

/** Reference a gradient by id as a fill/stroke value. */
export const url = (id: string): string => `url(#${id})`;
